import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn, formatDate } from "@/lib/utils";
import { getCurrentLanguageInClient, getValueFromTranslations, useRouterPush } from "@/lib/utils.client";
import { type Prop, type UserProp, UserPropStatus } from "@/modules/activity/prop/schema";
import type { Spu } from "@/modules/product/product.types.ts";

// Assumes that the findMyProps API returns UserProp along with its Prop definition
export interface UserPropWithDefinition extends UserProp {
  propDefinition: Prop;
  spuDetail?: Spu | null;
}
interface UserPropItemProps {
  userProp: UserPropWithDefinition;
  onViewDetails: (userProp: UserPropWithDefinition) => void;
}
export default function UserPropItem(t0) {
  const $ = _c(46);
  const {
    userProp,
    onViewDetails
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    propDefinition,
    startTime,
    endTime,
    status: userPropStatus
  } = userProp;
  const lang = getCurrentLanguageInClient();
  const routerPush = useRouterPush();
  const name = getValueFromTranslations(lang, propDefinition.name_trans);
  const getImageUrl = () => {
    if (userProp.spuDetail?.bannerImages && userProp.spuDetail.bannerImages.length > 0) {
      const bannerImage = userProp.spuDetail.bannerImages[0] as any;
      if (bannerImage.type === "video") {
        return getValueFromTranslations(lang, bannerImage.coverImage_trans);
      } else {
        if (bannerImage.type === "image") {
          return getValueFromTranslations(lang, bannerImage.link_trans);
        }
      }
    }
    return getValueFromTranslations(lang, propDefinition.icon_trans);
  };
  const iconUrl = getImageUrl();
  t("prop.exchangeGroup.birthdayRewards", "Birthday Rewards");
  t("prop.exchangeGroup.memberRewardsCoupon", "Member Rewards");
  t("prop.exchangeGroup.memberRewardsProp", "Member Rewards");
  let t1;
  if ($[0] !== endTime || $[1] !== startTime || $[2] !== t) {
    const getValidPeriodText = () => {
      const startDate = startTime;
      const endDate = endTime;
      if (startDate && endDate) {
        return `${formatDate(startDate)} - ${formatDate(endDate)}`;
      } else {
        if (startDate) {
          return t("prop.validPeriod.startDate", "Start date: {{date}}", {
            date: formatDate(startDate)
          });
        } else {
          if (endDate) {
            return t("prop.validPeriod.endDate", "Expired date: {{date}}", {
              date: formatDate(endDate)
            });
          }
        }
      }
      return "";
    };
    t1 = getValidPeriodText();
    $[0] = endTime;
    $[1] = startTime;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const validPeriod = t1;
  let t2;
  if ($[4] !== t || $[5] !== userPropStatus) {
    t2 = () => {
      switch (userPropStatus) {
        case UserPropStatus.EXPIRED:
          {
            return t("prop.status.expired", "EXPIRED");
          }
        case UserPropStatus.CONSUMED:
          {
            return t("prop.status.consumed", "USED");
          }
        default:
          {
            return "";
          }
      }
    };
    $[4] = t;
    $[5] = userPropStatus;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const getStatusText = t2;
  let t3;
  if ($[7] !== userPropStatus) {
    const getBackgroundColor = () => {
      if (userPropStatus === UserPropStatus.AVAILABLE) {
        return "bg-[#FAF1F1]";
      }
      return "bg-[#F8F8F8]";
    };
    t3 = cn("flex items-stretch", getBackgroundColor(), "p-3 md:p-4");
    $[7] = userPropStatus;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("flex-shrink-0 flex items-center justify-center bg-white border", "w-20 max-w-20 h-20 mr-3 md:w-[7.5rem] md:max-w-[7.5rem] md:h-[7.5rem] md:mr-6", "md:border-[#e5e5e5]");
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = iconUrl && iconUrl.trim() !== "" ? <Image src={iconUrl} alt={name} width={120} height={120} className="object-contain w-full h-full" /> : null;
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("font-['DINPro'] text-[#010101] text-left font-medium", "h-[1.125rem] text-[0.8125rem] leading-[1.125rem] md:h-5 md:text-[0.9375rem] md:leading-5");
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== name) {
    t8 = <div className={t7}>{name}</div>;
    $[14] = name;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="text-gray-500 text-xs md:text-sm mb-1 md:mb-2"> </div>;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== validPeriod) {
    t10 = validPeriod && <div className={cn("font-['DINPro'] text-[#010101] text-left font-normal", "h-[0.875rem] text-[0.625rem] leading-[0.875rem] md:h-[0.9375rem] md:text-[0.75rem] md:leading-[0.9375rem]")}>{validPeriod}</div>;
    $[17] = validPeriod;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t8) {
    t11 = <div>{t8}{t9}{t10}</div>;
    $[19] = t10;
    $[20] = t8;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn("mb-1 font-['DINPro'] text-[#010101] text-left font-normal underline", "h-[0.875rem] text-[0.8125rem] leading-[0.875rem] md:h-[0.9375rem] md:text-[0.9375rem] md:leading-[0.9375rem]");
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== onViewDetails || $[24] !== userProp) {
    t13 = () => onViewDetails(userProp);
    $[23] = onViewDetails;
    $[24] = userProp;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t) {
    t14 = t("common.viewDetails", "View details");
    $[26] = t;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t13 || $[29] !== t14) {
    t15 = <button className={t12} onClick={t13} type="button">{t14}</button>;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== getStatusText || $[32] !== routerPush || $[33] !== t || $[34] !== userPropStatus) {
    t16 = userPropStatus === UserPropStatus.AVAILABLE ? <Button onClick={() => routerPush("/shopping-cart")} className={cn("bg-destructive hover:bg-destructive/80", " px-3 py-1.5 text-xs md:text-sm md:px-4 md:py-2  w-15 md:w-auto h-6 md:h-auto")}>{t("prop.viewBag", "VIEW BAG")}</Button> : <div className={cn("font-['DINPro'] font-bold text-[#999999] text-right uppercase", "w-[3.9375rem] h-3 text-[0.75rem] leading-3")}>{getStatusText()}</div>;
    $[31] = getStatusText;
    $[32] = routerPush;
    $[33] = t;
    $[34] = userPropStatus;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t15 || $[37] !== t16) {
    t17 = <div className="flex items-end justify-between mt-2">{t15}{t16}</div>;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t11 || $[40] !== t17) {
    t18 = <div className="flex-1 flex flex-col justify-between">{t11}{t17}</div>;
    $[39] = t11;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t18 || $[43] !== t3 || $[44] !== t6) {
    t19 = <div className={t3}>{t6}{t18}</div>;
    $[42] = t18;
    $[43] = t3;
    $[44] = t6;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  return t19;
}